import type {
  CatalogueLine,
  CatalogueModule,
  LineId,
  LinePermissions,
  PermissionAction,
  PermissionMatrix,
  PermissionPayload,
  ResolvedPermissions,
} from '../types/rights-profile.types';

/** Per-module shortcut presets (see `applyModulePreset`). */
export type ModulePreset = 'readOnly' | 'fullAccess';

type Catalogue = readonly CatalogueLine[];

function indexByLine(catalogue: Catalogue): Map<LineId, CatalogueLine> {
  return new Map(catalogue.map(entry => [entry.line, entry]));
}

/** Direct children of a line (lines that name it among their parents). */
function childrenOf(catalogue: Catalogue): Map<LineId, LineId[]> {
  const children = new Map<LineId, LineId[]>();
  for (const entry of catalogue) {
    for (const parent of entry.parents) {
      const list = children.get(parent) ?? [];
      list.push(entry.line);
      children.set(parent, list);
    }
  }
  return children;
}

/** Empty matrix — every catalogue line present, nothing granted. */
export function buildEmptyMatrix(catalogue: Catalogue): PermissionMatrix {
  const matrix: PermissionMatrix = {};
  for (const { line } of catalogue) matrix[line] = {};
  return matrix;
}

/**
 * Create-page default — `display: true` on every line, nothing else (the spec's
 * initial state for a new profile; mirrors the backend display-only default).
 */
export function buildDefaultMatrix(catalogue: Catalogue): PermissionMatrix {
  const matrix: PermissionMatrix = {};
  for (const { line } of catalogue) matrix[line] = { display: true };
  return matrix;
}

/** A line's non-Display actions are locked while Display is off. */
export function isLineDisabled(perms: LinePermissions | undefined): boolean {
  return !perms?.display;
}

/** Walks a line's parent chain up to the roots (parents are always same-module). */
function ancestorsOf(index: Map<LineId, CatalogueLine>, line: LineId): LineId[] {
  const result: LineId[] = [];
  const stack = [...(index.get(line)?.parents ?? [])];
  while (stack.length > 0) {
    const current = stack.pop() as LineId;
    if (result.includes(current)) continue;
    result.push(current);
    stack.push(...(index.get(current)?.parents ?? []));
  }
  return result;
}

/** Transitive descendants of a line (everything that depends on it). */
function descendantsOf(children: Map<LineId, LineId[]>, line: LineId): LineId[] {
  const result: LineId[] = [];
  const stack = [...(children.get(line) ?? [])];
  while (stack.length > 0) {
    const current = stack.pop() as LineId;
    if (result.includes(current)) continue;
    result.push(current);
    stack.push(...(children.get(current) ?? []));
  }
  return result;
}

/**
 * Toggles one action of one line, enforcing the « no phantom right » rules
 * (HOMCO-1227) and returning a NEW matrix (never mutates):
 * - unchecking `display` clears the line AND everything that depends on it
 *   (its transitive descendants);
 * - checking any action turns `display` on for the line and along its parent
 *   chain (so a granted child always has a displayed parent);
 * - checking `display` additionally cascades `display` DOWN to every descendant
 *   (showing a fiche auto-shows its onglets, keeping their other grants) — HOMCO-326;
 * - otherwise the single action is set to `checked`.
 */
export function toggleAction(
  catalogue: Catalogue,
  matrix: PermissionMatrix,
  line: LineId,
  action: PermissionAction,
  checked: boolean,
): PermissionMatrix {
  const index = indexByLine(catalogue);
  const next: PermissionMatrix = { ...matrix };

  if (action === 'display' && !checked) {
    next[line] = {};
    const children = childrenOf(catalogue);
    for (const descendant of descendantsOf(children, line)) {
      next[descendant] = {};
    }
    return next;
  }

  const current = matrix[line] ?? {};
  if (checked) {
    next[line] = { ...current, display: true, [action]: true };
    for (const ancestor of ancestorsOf(index, line)) {
      next[ancestor] = { ...(next[ancestor] ?? {}), display: true };
    }
    if (action === 'display') {
      const children = childrenOf(catalogue);
      for (const descendant of descendantsOf(children, line)) {
        next[descendant] = { ...(next[descendant] ?? {}), display: true };
      }
    }
    return next;
  }

  next[line] = { ...current, [action]: false };
  return next;
}

/**
 * Applies a per-module shortcut to every line of `module`:
 * - `readOnly`   → `display` only;
 * - `fullAccess` → every applicable action of each line.
 * Coherent by construction (a module's parents are in the module), so no phantom
 * right. Returns a new matrix.
 */
export function applyModulePreset(
  catalogue: Catalogue,
  matrix: PermissionMatrix,
  module: CatalogueModule,
  preset: ModulePreset,
): PermissionMatrix {
  const next: PermissionMatrix = { ...matrix };
  for (const entry of catalogue) {
    if (entry.module !== module) continue;
    if (preset === 'readOnly') {
      next[entry.line] = { display: true };
    } else {
      const perms: LinePermissions = {};
      for (const action of entry.actions) perms[action] = true;
      next[entry.line] = perms;
    }
  }
  return next;
}

/**
 * Flattens the matrix into the API payload — one `{ line, action, enabled }` entry
 * per (line × applicable action), in catalogue order. Only a line's applicable
 * actions are emitted, so no « — » action ever reaches the backend (it would be
 * refused in 400).
 */
export function matrixToPayload(
  catalogue: Catalogue,
  matrix: PermissionMatrix,
): PermissionPayload[] {
  const payload: PermissionPayload[] = [];
  for (const entry of catalogue) {
    const perms = matrix[entry.line];
    for (const action of entry.actions) {
      payload.push({ line: entry.line, action, enabled: Boolean(perms?.[action]) });
    }
  }
  return payload;
}

/**
 * Overlays the flat effective rights (`GET /rights-profiles/:id` `permissions`,
 * same format as `/auth/me`) onto a full matrix, so every catalogue line exists.
 * Used when editing: a line absent from the response becomes an empty (all-off)
 * entry, and the UI renders every checkbox.
 */
export function hydratePermissionMatrix(
  catalogue: Catalogue,
  resolved: ResolvedPermissions,
): PermissionMatrix {
  const matrix: PermissionMatrix = {};
  for (const entry of catalogue) {
    const granted = resolved[entry.line];
    const perms: LinePermissions = {};
    if (granted) for (const action of granted) perms[action] = true;
    matrix[entry.line] = perms;
  }
  return matrix;
}

/** Granted vs total applicable actions of a module — drives the « X/Y » counter. */
export function countModuleGrants(
  catalogue: Catalogue,
  matrix: PermissionMatrix,
  module: CatalogueModule,
): { granted: number; total: number } {
  let granted = 0;
  let total = 0;
  for (const entry of catalogue) {
    if (entry.module !== module) continue;
    const perms = matrix[entry.line];
    for (const action of entry.actions) {
      total += 1;
      if (perms?.[action]) granted += 1;
    }
  }
  return { granted, total };
}
